import { useState, type ReactNode } from "react";
import { isAddress } from "ethers";

function short(value: string, left = 6, right = 4): string {
  if (value.length <= left + right + 3) return value;
  return `${value.slice(0, left)}…${value.slice(-right)}`;
}

export function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  return (
    <button className="icon-button" type="button" onClick={() => void copy()} title={`Copy ${label}`}>
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export function AddressView({
  address,
  explorerUrl,
  full = false,
}: {
  address: string;
  explorerUrl: string;
  full?: boolean;
}) {
  if (!isAddress(address)) return <span className="mono muted">Not configured</span>;
  return (
    <span className="address-view">
      <span className="mono" title={address}>
        {full ? address : short(address)}
      </span>
      <CopyButton value={address} label="address" />
      <a
        className="icon-link"
        href={`${explorerUrl}/address/${address}`}
        target="_blank"
        rel="noreferrer"
        title="Open address in L1X Explorer"
      >
        Explorer ↗
      </a>
    </span>
  );
}

export function HashView({ value }: { value: string }) {
  return (
    <span className="hash-view">
      <code title={value}>{value}</code>
      <CopyButton value={value} label="Safe transaction hash" />
    </span>
  );
}

export function TxView({ hash, explorerUrl }: { hash: string; explorerUrl: string }) {
  return (
    <span className="address-view">
      <span className="mono" title={hash}>
        {short(hash, 10, 8)}
      </span>
      <CopyButton value={hash} label="transaction hash" />
      <a
        className="icon-link"
        href={`${explorerUrl}/tx/${hash}`}
        target="_blank"
        rel="noreferrer"
      >
        Explorer ↗
      </a>
    </span>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="empty-state">{children}</div>;
}

export function SpinnerLabel({ children }: { children: ReactNode }) {
  return (
    <span className="spinner-label">
      <span className="spinner" aria-hidden="true" />
      {children}
    </span>
  );
}
